/**
 * Cookie Consent Service
 *
 * Simplified consent management for bifrost (app.deepsource.com).
 * Phase 1: Auto-consent all users since they've already consented on the marketing site.
 */

import type { CookieConsent, ConsentCategory } from '~/types/tracking'

const CONSENT_STORAGE_KEY = 'cookie_consent'
const CONSENT_VERSION = '1.0'

let instance: CookieConsentService | null = null

export class CookieConsentService {
  private consent: CookieConsent | null = null

  constructor() {
    this.loadConsent()

    // Phase 1: Auto-consent if no stored consent
    if (!this.consent) {
      this.autoConsent()
    }
  }

  static getInstance(): CookieConsentService {
    if (!instance) {
      instance = new CookieConsentService()
    }
    return instance
  }

  hasConsent(category: ConsentCategory): boolean {
    if (!this.consent) return false
    return this.consent[category] === true
  }

  getConsent(): CookieConsent | null {
    return this.consent
  }

  private loadConsent(): void {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as CookieConsent
        if (parsed.version === CONSENT_VERSION) {
          this.consent = parsed
        } else {
          localStorage.removeItem(CONSENT_STORAGE_KEY)
        }
      }
    } catch {
      // Silently fail — localStorage may not be available
    }
  }

  private autoConsent(): void {
    const consentState: CookieConsent = {
      necessary: true,
      analytics: true,
      advertising: true,
      timestamp: Date.now(),
      version: CONSENT_VERSION
    }
    this.consent = consentState
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentState))
    } catch {
      // Silently fail
    }
  }
}
